// Organisatie · Jurisdictietaal instellingen (P3, legacy Organisatiegegevens sub-tab) — declare which
// jurisdictions the org operates in + the content language per jurisdiction. Reads/writes the singleton
// /jurisdiction-language endpoint (tenant columns + config.jurisdictionLanguages). The primary jurisdiction
// is read-only (set at provisioning; foundational for language routing). Native <select> for reliability.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);
const selectCls = "h-8 w-44 rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30";

type Jur = { id: string; code: string; displayName: string; isPrimary?: boolean; language?: string };
type Lang = { code: string; displayName: string; nativeName: string };
type Settings = { primaryJurisdictionId: string; jurisdictions: Jur[]; availableJurisdictions: Jur[]; availableLanguages: Lang[] };

export function JurisdictietaalRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["jurisdiction-language"], queryFn: () => api.getJurisdictionLanguageSettings() as Promise<Settings> });
  const data = q.data ?? null;

  // local edit state, seeded from the server data on first load.
  const [additional, setAdditional] = useState<Set<string> | null>(null);
  const [langs, setLangs] = useState<Record<string, string> | null>(null);
  const seededAdditional = additional ?? new Set((data?.jurisdictions ?? []).filter((j) => !j.isPrimary).map((j) => j.id));
  const seededLangs = langs ?? Object.fromEntries((data?.jurisdictions ?? []).map((j) => [j.id, j.language ?? "nl"]));

  const save = useMutation({
    mutationFn: () => api.updateJurisdictionLanguageSettings({
      additionalJurisdictionIds: [...seededAdditional],
      jurisdictionLanguages: seededLangs,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jurisdiction-language"] }); setAdditional(null); setLangs(null); },
  });

  const toggle = (id: string) => {
    const next = new Set(seededAdditional);
    next.has(id) ? next.delete(id) : next.add(id);
    setAdditional(next);
  };
  const setLang = (id: string, code: string) => setLangs({ ...seededLangs, [id]: code });

  // operative = primary + the toggled-on additional jurisdictions
  const operative = (data?.availableJurisdictions ?? []).filter((j) => j.id === data?.primaryJurisdictionId || seededAdditional.has(j.id));

  return (
    <section>
      <PageHeader title={t("jt.title")} description={t("jt.sub")} help={t("help.jurisdictietaal")} />
      {q.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(q.error)}</p>}

      <div className="grid gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t("jt.jurisdiction")} &amp; {t("jt.language")}</CardTitle></CardHeader>
          <CardContent>
            {q.isLoading ? <Skeleton className="h-24 w-2/3" /> : (data?.availableJurisdictions ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("jt.none")}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {(data?.availableJurisdictions ?? []).map((j) => {
                  const isOperative = operative.some((o) => o.id === j.id);
                  const isPrimary = j.id === data?.primaryJurisdictionId;
                  return (
                    <div key={j.id} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                      <label className="inline-flex min-w-0 items-center gap-2">
                        <input type="checkbox" checked={isOperative} disabled={isPrimary || save.isPending} onChange={() => toggle(j.id)} />
                        <span className="truncate font-medium">{j.displayName}</span>
                        {isPrimary && <Badge variant="secondary">{t("jt.primary")}</Badge>}
                      </label>
                      {isOperative && (
                        <select className={selectCls} value={seededLangs[j.id] ?? "nl"} disabled={save.isPending} onChange={(e) => setLang(j.id, e.target.value)} aria-label={`${j.displayName} — ${t("jt.language")}`}>
                          {(data?.availableLanguages ?? []).map((l) => <option key={l.code} value={l.code}>{l.nativeName}</option>)}
                        </select>
                      )}
                    </div>
                  );
                })}
                <Can perm="tenant_settings.update">
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? t("verw.saving") : t("jt.save")}</Button>
                    {save.isSuccess && <span className="text-sm text-green-700">{t("jt.saved")}</span>}
                    {save.isError && <span className="text-sm text-destructive">{errText(save.error)}</span>}
                  </div>
                </Can>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
